/**
 * 面向用户的全部文案。
 *
 * 这是单一语言产品，**不引入 i18n 库** —— 集中在这里的理由不是国际化，
 * 而是让文案能被单独审阅：组织者想核对「用户到底看到哪些字」时，
 * 只看这一个文件就够了，不必翻十几个组件。
 *
 * 术语沿用 design.md 的原词，不要另造同义词：
 *   打卡（非签到）、赛道（非分类）、驳回（非拒绝）、审核（非审查）、
 *   证明材料（非附件）、激活码（非邀请码）。
 * 组织者在微信群推文里用的就是这套词，界面必须与之一致。
 */
export const zh = {
  app: {
    name: '国庆打卡',
    subtitle: '化学与分子工程学院',
  },

  nav: {
    /** 底部导航整体的无障碍名称，读屏用户会先听到它 */
    label: '主导航',
    home: '首页',
    records: '记录',
    leaderboard: '排行榜',
    me: '我的',
  },

  dateTime: {
    /** 0 = 周日。索引与 Date.getDay() 对齐 */
    weekdays: ['周日', '周一', '周二', '周三', '周四', '周五', '周六'] as const,
    deadlinePassed: '已截止',
    remainingHoursMinutes: (hours: number, minutes: number) => `${hours} 小时 ${minutes} 分`,
    remainingMinutesSeconds: (minutes: number, seconds: number) => `${minutes} 分 ${seconds} 秒`,
    remainingSeconds: (seconds: number) => `${seconds} 秒`,
  },

  upload: {
    networkError: '网络异常，请检查网络后重试',
    timeout: '上传超时，请重试',
    aborted: '已取消上传',
    failed: (status: number) => `上传失败（${status}）`,
    requestFailed: (status: number) => `请求失败（${status}）`,
    sessionExpired: '登录状态已失效',
  },

  common: {
    retry: '重新加载',
    backHome: '回到首页',
    backToRecords: '返回记录列表',
    submit: '提交',
    retrySubmit: '重试提交',
    /** 仅用于真的没连上服务端的场景；服务端有响应时用它的 message */
    loadFailed: '请检查网络后重试。',
    /** 409 CAMPAIGN_NOT_ACTIVE：没有活动可加载，不是网络故障 */
    noCampaign: '当前没有进行中的活动',
    noCampaignDetail: '活动尚未开放、已结束或已归档；发布新活动后重新加载本页即可。',
    trackingId: (id: string) => `追踪号 ${id}`,
  },

  /** 首页与「我的」页最下方的赞助商展示位。图片目录见 src/assets/sponsors/ */
  sponsor: {
    /**
     * 展示位上方小字的前半句。结尾的冒号是产品指定的，别改标点。
     * 冒号后面那半句是 thanksBrand —— 分成两句是**为了只把名字加粗**：
     * 一句连起来就只能在整行上加粗，小字跟着变黑，喧宾夺主。
     */
    thanks: '特别感谢本次活动的赞助：',
    /** 冒号后面的赞助商名字，渲染时加粗 */
    thanksBrand: '一式化学TBN',
    /** 图片文件名里给不出名字时的兜底 alt */
    alt: '赞助商图片',
  },

  auth: {
    login: {
      title: '登录',
      subtitle: '使用学号与密码登录',
      studentId: '学号',
      studentIdPlaceholder: '请输入学号',
      studentIdRequired: '请填写学号',
      password: '密码',
      passwordPlaceholder: '请输入密码',
      passwordRequired: '请填写密码',
      submit: '登录',
      notActivatedYet: '还没有激活？',
      goActivate: '去激活',
      forgotPassword: '忘记密码请联系活动管理员重置。',
    },
    activate: {
      title: '激活账号',
      subtitle: '使用管理员发放的学号与激活码完成首次激活',
      studentId: '学号',
      studentIdPlaceholder: '请输入学号',
      studentIdRequired: '请填写学号',
      activationCode: '激活码',
      activationCodePlaceholder: '请输入激活码',
      activationCodeRequired: '请填写激活码',
      password: '设置密码',
      passwordPlaceholder: '至少 8 位，含字母与数字',
      passwordRequired: '请设置密码',
      passwordMin: (n: number) => `密码至少 ${n} 位`,
      passwordMax: (n: number) => `密码不能超过 ${n} 位`,
      passwordNeedsLetter: '密码需要包含字母',
      passwordNeedsDigit: '密码需要包含数字',
      confirm: '确认密码',
      confirmPlaceholder: '再次输入密码',
      confirmRequired: '请再次输入密码',
      confirmMismatch: '两次输入的密码不一致',
      submit: '激活并登录',
      alreadyActivated: '已经激活过？',
      goLogin: '去登录',
      nameLockNotice: '激活后学号与姓名不能自行修改，如需更正请联系管理员。',
    },
    profile: {
      title: '我的',
      name: '姓名',
      studentId: '学号',
      nameLockNotice: '学号与姓名如需更正，请联系活动管理员。',
      changePassword: '修改密码',
      currentPassword: '当前密码',
      currentPasswordRequired: '请填写当前密码',
      newPassword: '新密码',
      newPasswordPlaceholder: '至少 8 位，含字母与数字',
      newPasswordRequired: '请设置新密码',
      newPasswordMin: (n: number) => `新密码至少 ${n} 位`,
      newPasswordNeedsLetter: '新密码需要包含字母',
      newPasswordNeedsDigit: '新密码需要包含数字',
      confirmNewPassword: '确认新密码',
      confirmRequired: '请再次输入新密码',
      confirmMismatch: '两次输入的密码不一致',
      savePassword: '保存新密码',
      passwordChanged: '密码已修改，其他设备需要重新登录',
      logout: '退出登录',
      logoutConfirmTitle: '确认退出登录？',
      logoutOk: '退出',
      logoutCancel: '取消',
      /** 操作指导的重看入口。引导本身每个浏览器只演示一遍，想复习只能从这里进 */
      guide: '操作指导',
      guideCheckin: '打卡流程说明',
      guideLeaderboard: '排行榜说明',
    },
  },

  checkin: {
    /**
     * §5「可同时作为参赛者」：审核员与超管可能压根不在名单里。
     *
     * 首页与记录页共用这一个空状态 —— 它不是错误，也不该被说成权限不足，
     * 否则用户只知道自己「不能看」，不知道原因，也不知道该怎么办。
     */
    notParticipant: '当前账号不是本次活动的参赛者。',
    notParticipantHint: '如需参赛，请联系管理员把你加入参赛名单。',

    /** §7.3 的九种卡片状态：文字必须两两不同（有单测守着） */
    cardState: {
      before_open: '今日打卡尚未开放',
      can_submit: '今日尚未打卡',
      submit_closed: '活动已停止提交',
      pending: '已提交，等待审核',
      approved: '今日打卡有效',
      rejected_open: '已驳回',
      rejected_closed: '今日打卡无效（已截止）',
      missed: '今日未完成',
      invalid: '记录已失效',
    },
    cardAction: {
      submit: '去打卡',
      resubmit: '重新提交',
      detail: '查看详情',
    },
    /** §6.4 记录的四种结论状态 */
    status: {
      pending: '待审核',
      approved: '已通过',
      rejected: '已驳回',
      revoked: '已撤销',
      void: '已作废',
    },
    home: {
      validDaysSummary: (days: number) => `累计有效 ${days} 天`,
      countdown: (remaining: string) => `距今日截止 ${remaining}`,
      deadlineHint: (time: string) => `今日截止 ${time}`,
      deadlineCountdownHint: (remaining: string) => `距截止 ${remaining}`,
      validDaysAndScore: (days: number, score: string) => `有效 ${days} 天 · 积分 ${score}`,
      rejectionPrefix: (reason: string) => `驳回原因：${reason}`,
      campaignSettling: '活动已进入结算阶段，停止提交打卡',
      campaignFinished: '活动已结束',
      campaignNotOpen: '活动当前未开放打卡',
      campaignStatusDetail: '已有记录的审核结果仍可查看。',
      leaderboardCadence: (time: string) =>
        `排行榜每日 ${time} 更新，今日通过的记录将在下次更新后计入。`,
      loadFailed: '没能加载今日打卡',
    },
    submit: {
      proofRequirement: '有效证明要求',
      materials: '证明材料',
      addImage: '添加图片',
      note: '文字备注',
      notePlaceholder: '可选，补充说明本次打卡的内容',
      minImages: (n: number) => `至少需要 ${n} 张证明材料`,
      /*
        申报明细（design.md §9.1）。单词与运动必须填，读书不填 ——
        码与阈值的前端镜像在 features/checkin/declaration.ts。
      */
      wordCount: '背诵的单词数量',
      wordCountPlaceholder: '例如 50',
      wordCountRequired: '请填写当日背诵的单词数量',
      wordCountTooFew: (min: number) => `单词数量不能少于 ${min} 个`,
      wordCountTooMany: (max: number) => `单词数量不能超过 ${max} 个`,
      exerciseType: '本次运动类型',
      exerciseTypeRequired: '请选择本次运动类型',
      /** 读书赛道的材料要求放宽了：图片与备注有一个即可 */
      readingNeedsOne: '请上传证明材料或填写备注，二者至少填一个',
      uploading: '正在上传证明材料…',
      processing: '服务器正在处理图片…',
      submitted: '已提交，等待审核',
      alreadySubmitted: '这次提交此前已经完成',
      resubmittedNotice: '提交后可在今日截止前重新提交，审核仅以最新一次为准。',
      reopenBanner: (time: string) => `管理员已临时重新开放至 ${time}`,
      reopenDetail: '请在此时间前完成提交。',
      alreadyApprovedTitle: '该记录已审核通过',
      alreadyApprovedDetail: '如需修改，请联系管理员重新打开该记录。',
      invalidTitle: '该记录已失效',
      invalidDetail: '该记录已被管理员处置，无法再次提交。',
      closedTitle: '活动已停止提交',
      missedTitle: '今日打卡已截止',
      closedDetail: '本活动日的打卡不能再提交或修改。',
      trackNotFound: '赛道不存在',
      trackNotFoundDetail: '该赛道不在此活动中，或已停用。',
      loadFailed: '没能加载打卡信息',
      dragToReorder: ' · 拖动缩略图可调整顺序',
      /** 上传规则摘要，由活动配置拼出：1–3 张，JPEG、PNG、WebP，单张不超过 640 KB（超出会自动压缩） */
      uploadRules: (min: number, max: number, formats: string, limit: string) =>
        `${min}–${max} 张，${formats}，单张不超过 ${limit}（超出会自动压缩）`,
      sizeLimitMb: (limitMb: number) => `${limitMb} MB`,
      tooManyImages: (max: number) => `最多只能上传 ${max} 张图片`,
      ignoredExtra: (room: number) => `最多还能再加 ${room} 张，已忽略多余的图片`,
      notAnImage: (name: string) => `「${name}」不是有效的图片，可能只是改了扩展名`,
      unsupportedFormat: (name: string, mime: string) => `「${name}」是 ${mime}，当前活动不接受该格式`,
      /** 选完图后在浏览器里压缩的耗时，手机上每张几百毫秒到一两秒 */
      preparing: '正在处理图片…',
      /** 压缩之后的兜底：极限情况下（比如满屏噪点）确实压不到限额以内 */
      tooLargeAfterCompress: (name: string, size: string, limit: string) =>
        `「${name}」压缩后仍有 ${size}，超过单张 ${limit} 的限制`,
      /** 大到连解码都不敢试，避免把手机上的标签页撑爆 */
      tooLargeToProcess: (name: string, size: string) =>
        `「${name}」${size}，超出可处理的范围，请先用其他工具压缩后再上传`,
      /** 浏览器解不开：文件损坏，或不是 JPEG/PNG/WebP 里的一种 */
      cannotProcess: (name: string) => `「${name}」无法处理，请换一张图片`,
      deleteImage: (index: number) => `删除第 ${index + 1} 张`,
      proofAlt: (index: number) => `证明材料 ${index + 1}`,
    },
    /**
     * 运动类型的显示名。键是服务端的稳定英文码
     * （server/src/services/judge.service.ts 的 FITNESS_EXERCISE_TYPES）——
     * 存码不存中文，改名才不用洗数据。
     * 提交页与审核页共用这一份，未知码由代码兜底显示原码而不是空白。
     */
    exerciseTypes: {
      run_gt_2km: '>2km跑步',
      run_gt_3km: '>3km跑步',
      workout_30min: '一般运动30min',
      workout_60min: '一般运动60min',
    },
    records: {
      title: '打卡记录',
      allTracks: '全部赛道',
      allStatuses: '全部状态',
      submittedAt: (time: string) => `提交于 ${time}`,
      assetCount: (n: number) => `${n} 张`,
      total: (n: number) => `共 ${n} 条记录`,
      loadMore: (shown: number, total: number) => `加载更多（已显示 ${shown} / ${total}）`,
      empty: '还没有打卡记录，去首页完成第一次打卡吧。',
      emptyFiltered: '当前筛选条件下没有记录。',
      clearFilters: '清除筛选',
      loadFailed: '没能加载打卡记录',
      rejectionPrefix: (reason: string) => `驳回原因：${reason}`,
    },
    detail: {
      noAssets: '本条记录没有证明材料。',
      submitInfo: '提交信息',
      submittedAt: '提交时间',
      reviewedAt: '审核时间',
      notReviewed: '尚未审核',
      revision: '版本',
      revisionN: (n: number) => `第 ${n} 版`,
      note: '备注',
      history: (n: number) => `提交历史（${n} 个较早版本）`,
      historyItem: (n: number, count: number) => `第 ${n} 版 · ${count} 张`,
      historyLatestOnly: '审核仅以最新一次提交为准。',
      rejectedTitle: '本条记录被驳回',
      revokedTitle: '审核结果已被管理员撤销',
      revokedDetail: '该记录不再计分。如有疑问请联系活动管理员。',
      voidTitle: '该记录已被作废',
      voidDetail: '如有疑问请联系活动管理员。',
      reopenTitle: '管理员已临时重新开放',
      reopenDetail: (time: string) => `请在此时间前完成重新提交：${time}`,
      loadFailed: '没能加载记录详情',
      imageLoadFailed: '图片加载失败',
      reloadImage: '重新加载',
      viewLarger: '点击查看大图',
    },
    /** 赛道图标：后端给的是自由字符串，必须带兜底 */
    trackIconFallback: '📌',
  },

  leaderboard: {
    title: '排行榜',
    overall: '总榜',
    cadence: (time: string) => `每日 ${time} 更新，已通过的记录将在下次更新后计入`,
    countedThrough: (date: string, generatedAt: string) => `统计至 ${date} · 生成于 ${generatedAt}`,
    finalBadge: '最终榜单',
    snapshotFailed: '最近一次快照生成失败，当前显示的仍是上一份有效数据。',
    empty: '这个榜单暂时没有数据。',
    notGenerated: '排行榜尚未生成，请等待管理员完成第一次统计。',
    me: '我',
    /** 桌面下「我的名次」是右栏的一张卡片，需要标题；手机上它是贴底浮条，标题被 CSS 隐藏 */
    myRankTitle: '我的名次',
    meMeta: (className: string, days: number) => `${className} · 有效 ${days} 天`,
    /*
      算分说明。常驻在榜单上方，不只在引导气泡里出现 ——
      引导关掉之后就再也看不到了，而「分是怎么算的」是要反复回看的内容。

      由 explainScoring()（src/features/guide/scoringHint.ts）按活动配置挑选：
      服务端赛道的 daily_points / daily_cap / campaign_cap / overall_weight
      决定走哪一条，见 server/src/config/campaign.ts 的 DEFAULT_TRACK_RULE。
      改那里的评分默认值，这里必须跟着改。
    */
    scoringRule: (trackCount: number) =>
      `读书每次 1 分；单词按背诵数量、运动按运动类型分档，每次 1–2 分。` +
      `${trackCount} 条赛道等权，总榜为各赛道积分之和。`,
    /** 权重或上限被改过时的兜底：不编造具体数字，只说清结构 */
    scoringFallback: '总榜分数由各赛道分数按权重折算得出。',
    loadMore: (shown: number, total: number) => `加载更多（已显示 ${shown} / ${total}）`,
    loadFailed: '没能加载排行榜',
  },

  admin: {
    nav: {
      dashboard: '概览',
      review: '审核',
      participants: '名单',
      accounts: '账号',
      ops: '异常处理',
      audit: '审计日志',
    },
    collapseNav: '收起导航',
    expandNav: '展开导航',
    participantView: '参赛者视图',
    toParticipantView: '切换到参赛者端（审核员也可以给自己打卡）',
    enterAdmin: '进入管理后台',

    /** 活动状态的展示文案（§6.1） */
    campaignStatus: {
      draft: '草稿',
      published: '已发布',
      active: '进行中',
      settling: '结算中',
      finished: '已结束',
      archived: '已归档',
    },

    common: {
      loadFailed: '没能加载数据',
      refresh: '刷新',
      empty: '暂无数据',
      total: (n: number) => `共 ${n} 条`,
      loadingMore: '正在加载更多…',
      reason: '原因',
      reasonRequired: '请填写原因',
      reasonTooShort: '原因至少 2 个字',
      reasonPlaceholder: '请说明原因，会写入审计日志',
      cancel: '取消',
      confirm: '确认',
      notParticipant: '未匹配到参赛者',
    },

    dashboard: {
      title: '概览',
      participants: '参赛人数',
      participantsDetail: (activated: number, pending: number, disabled: number) =>
        `已激活 ${activated} · 待激活 ${pending} · 已禁用 ${disabled}`,
      todaySubmitted: '今日提交',
      pendingReview: '待审核',
      todayApproved: '今日通过',
      todayRejected: '今日驳回',
      trackRates: '各赛道今日提交率',
      trackRow: (submitted: number) => `今日提交 ${submitted} 人`,
      submissionRate: (rate: number) => `${(rate * 100).toFixed(1)}%`,
      trackDisabled: '已停用',
      nextLeaderboard: '距下次排行榜更新',
      lastJob: '最近一次定时任务',
      jobMeta: (processed: number) => `处理 ${processed} 条`,
      noJobRun: '尚无任务记录',
      warnings: '需要关注',
      warningKind: {
        job_failed: '定时任务失败',
        snapshot_failed: '排行榜快照失败',
        pending_before_freeze: '结算前仍有积压',
      },
      scheduledJobs: '调度计划',
      jobName: '任务',
      jobExpression: 'cron',

      snapshot: '最近一次排行榜快照',
      snapshotCutoff: (date: string) => `统计截至 ${date}`,
      snapshotAt: (time: string) => `生成于 ${time}`,
      snapshotFinal: '已冻结',
      noSnapshot: '还没有生成过排行榜快照。',
      nextUpdateAt: (time: string) => `${time} 更新`,

      jobStatus: {
        running: '运行中',
        success: '成功',
        failed: '失败',
        skipped_locked: '已跳过（未抢到锁）',
      },
      jobTrigger: {
        manual: '手动',
        cron: '定时',
      },
      recentRuns: '最近任务',
      runsEmpty: '还没有任务执行记录。',
      runWindow: (startedAt: string, finishedAt: string | null) =>
        finishedAt ? `${startedAt} → ${finishedAt}` : `${startedAt} 起`,
      trigger: '立即运行',
      triggerConfirmTitle: (name: string) => `手动触发「${name}」？`,
      triggerConfirmBody:
        '任务会立即执行一次。整点快照与手动触发共用同一把锁，正在运行时这次会被跳过。',
      triggerDone: (processed: number) => `任务完成，处理 ${processed} 条`,
      triggerFailed: '任务执行失败',
    },

    review: {
      title: '审核',
      queue: '待审核队列',
      queueEmpty: '待审核队列已清空',
      queueEmptyFiltered: (pending: number) =>
        `当前筛选条件下没有记录，但队列中还有 ${pending} 条 —— 试试清除筛选`,
      clearFilters: '清除筛选',
      allTracks: '全部赛道',
      allClasses: '全部班级',
      activityDate: '活动日',
      filterDate: '按活动日筛选',
      progress: '审核进度',
      progressPending: '待审核',
      progressReviewed: '今日已审',
      progressApproved: '今日通过',
      progressRejected: '今日驳回',
      progressNote: '这里的数字是整场活动的，不受左侧筛选影响',
      materials: '证明材料',
      noMaterials: '这条记录没有证明材料。',
      manualNoMaterials: '这条记录是管理员补录的，没有参赛者上传的证明材料。',
      materialsAlt: (n: number) => `证明材料第 ${n} 张`,
      switchAsset: (n: number) => `查看第 ${n} 张`,
      assetIndex: (current: number, total: number) => `第 ${current} / ${total} 张`,
      zoomIn: '放大（+）',
      zoomOut: '缩小（-）',
      rotate: '旋转 90°',
      resetView: '恢复原始比例',
      fullscreen: '全屏查看',
      exitFullscreen: '退出全屏（Esc）',
      context: '记录信息',
      participant: '参赛者',
      studentId: '学号',
      name: '姓名',
      className: '班级',
      phoneSuffix: '手机尾号',
      remark: '备注',
      track: '赛道',
      submittedAt: '提交时间',
      revision: '版本',
      revisionN: (n: number) => `第 ${n} 版`,
      manualBadge: '补录',
      resubmissionBadge: (n: number) => `第 ${n} 次提交`,
      reopenNotice: (time: string) => `已重新开放至 ${time}`,
      note: '参赛者备注',
      noNote: '（未填写）',
      /*
        申报明细与判定分值（design.md §9.1）。梯度化之后同一个「通过」按钮
        对应的分值是变的，这两行是审核员判断该不该通过的依据之一。
      */
      wordCountLine: (n: number) => `单词数量：${n} 个`,
      exerciseTypeLine: (label: string) => `运动类型：${label}`,
      judgedPointsLine: (points: string) => `本次计分：${points} 分`,
      history: '审核历史',
      noHistory: '还没有审核记录',
      previousRevisions: (n: number) => `较早的提交版本（${n}）`,
      previousRevisionItem: (n: number, count: number) => `第 ${n} 版 · ${count} 张`,
      actionLabel: {
        approve: '通过',
        reject: '驳回',
        reopen: '重新开放',
        revoke: '撤销',
        void: '作废',
        manual_create: '管理员补录',
      },
      approve: '通过',
      reject: '驳回',
      prev: '上一份',
      next: '下一份',
      position: (current: number, total: number) => `第 ${current} / ${total} 条`,
      noSelection: '从左侧队列中选择一条记录开始审核',
      /** 中栏与右栏在队列为空时的提示。说「为空」而不是「已清空」——
          后者暗示是审阅者自己干完的，而筛选也可能筛空 */
      emptyQueue: '待审核队列为空',
      approveSuccess: '已通过',
      rejectSuccess: '已驳回',
      rejectTitle: '驳回',
      rejectReasonLabel: '驳回原因',
      rejectDetailLabel: '补充说明',
      rejectDetailPlaceholder: '选择「其他」时必须填写',
      rejectDetailRequired: '选择「其他」时请填写补充说明',
      hotkeyHint: '快捷键',
      hotkeyApprove: 'A',
      hotkeyReject: 'R',
      hotkeyPrev: 'K',
      hotkeyNext: 'J',
      shortcutHint: 'J/K 切换 · A 通过 · R 驳回 · 数字键选择驳回原因 · Esc 取消',
      conflictTitle: '该记录已被其他人修改',
      conflictDetail: (version: number) =>
        `你打开这条记录之后，它的状态发生了变化（当前版本 v${version}）。请刷新后继续。`,
      conflictRefresh: '刷新并继续',
      conflictSkip: '跳到下一份',
      detailStale: '这条记录有更新',
      detailStaleRefresh: '刷新',
      loadDetailFailed: '没能加载记录详情',
      loadQueueFailed: '没能加载审核队列',
    },

    participants: {
      title: '名单管理',
      subtitle: '导入参赛名单、发放激活码、处理账号状态。',

      add: '添加参赛者',
      import: '导入名单',
      exportRoster: '导出名册',
      exportCodes: '导出激活状态',
      downloadTemplate: '下载 CSV 模板',
      templateHint: '模板列：学号、姓名、班级、手机尾号、备注。文件带 BOM，Excel 打开中文不会乱码。',

      search: '按学号或姓名搜索',
      allStatus: '全部状态',
      allClasses: '全部班级',
      classNamePlaceholder: '按班级筛选',

      studentId: '学号',
      name: '姓名',
      className: '班级',
      status: '参赛状态',
      accountStatus: '账号状态',
      activation: '激活',
      phoneSuffix: '手机尾号',
      remark: '备注',
      joinedAt: '加入时间',
      actions: '操作',

      /** 参赛者在本活动内的状态（§6） */
      statusLabel: {
        active: '在册',
        disabled: '已禁用',
        anonymized: '已匿名化',
      },
      /** 账号级状态。与上面**不是**一回事，见 AdminParticipant 的说明 */
      accountLabel: {
        pending_activation: '待激活',
        active: '正常',
        disabled: '已禁用',
      },
      activated: '已激活',
      notActivated: '未激活',
      anonymizedHint: '姓名等信息已抹除',

      regenerateCode: '重新生成激活码',
      resetPassword: '重置密码',
      disable: '禁用',
      enable: '启用',
      anonymize: '匿名化',

      /** 重新生成会作废此前的码，必须说清 */
      regenerateConfirm: '重新生成激活码？',
      regenerateConfirmBody:
        '这个账号此前所有未使用的激活码会立即作废。已激活的账号不受影响，但重新生成的码只有对方再次激活才会用上。',
      regenerateDone: '已生成新激活码，旧码已作废',

      resetConfirm: '重置密码？',
      resetConfirmBody:
        '会生成一个临时密码并撤销该账号的所有登录状态。尚未激活的账号会因此直接变成已激活。',
      resetDone: '已生成临时密码',

      disableConfirm: '禁用这个账号？',
      disableConfirmBody: '该账号将无法登录，已登录的会话会被立即撤销。记录与积分不受影响。',
      disableDone: '账号已禁用',
      disableRevoked: (n: number) => `已一并撤销 ${n} 个登录会话`,
      enableDone: '账号已启用',

      anonymizeConsequence:
        '匿名化会抹除姓名、班级与学号快照，且不可恢复 —— 这是「已有记录的参赛者不允许删除」的替代方案。',
      anonymizeDeleteEvidence: '同时删除证明材料',
      anonymizeDeleteEvidenceHint:
        '截图里往往带着姓名或账号，留着它们等于匿名化没做完。只有在争议尚未了结时才关掉。',
      anonymizeConfirm: '匿名化',
      anonymizeDone: (deletedAssets: number) => `已匿名化，删除 ${deletedAssets} 份证明材料`,

      /** 一次性明文的展示。措辞必须让人意识到关掉就没了 */
      codeTitle: '激活码（只显示这一次）',
      codeWarning:
        '关闭后无法再查看 —— 系统只保存哈希值。现在就把它们交给本人；忘了的话只能重新生成（会使旧码作废）。',
      codeFor: (name: string, studentId: string) => `${name}（${studentId}）`,
      codeCopy: '复制',
      codeCopied: '已复制',
      codeCopyAll: '复制全部',
      codeDownload: '下载为 CSV',
      codeColumnStudentId: '学号',
      codeColumnName: '姓名',
      codeColumnCode: '激活码',
      passwordTitle: '临时密码（只显示这一次）',
      passwordWarning: '关闭后无法再查看。请通过可靠渠道交给本人，并提醒对方尽快修改。',
      passwordLabel: '临时密码',
      close: '关闭',

      /** 导入流程 */
      importTitle: '导入名单',
      importStepSelect: '选择文件',
      importDropHint: '选择或拖入 CSV 文件（最大 5 MB）',
      importReadTemplate: '还没有模板？',
      importTooLarge: '文件超过 5 MB，请拆分后再导入。',
      importPreviewing: '正在校验…',
      importStepPreview: '预览',
      importStepResult: '结果',
      importSummary: (total: number, valid: number, invalid: number) =>
        `共 ${total} 行：${valid} 行可导入${invalid > 0 ? `，${invalid} 行有问题` : ''}`,
      importExisting: (n: number) => `其中 ${n} 行学号已存在，提交后是更新而不是新建`,
      importDuplicates: (n: number) => `其中 ${n} 行在文件内重复，只导入第一次出现的那条`,
      importRowsTruncated: (limit: number) =>
        `预览只列出前 ${limit} 行，上面的汇总数字是完整的。`,
      importLine: (line: number) => `第 ${line} 行`,
      importRowOk: '可导入',
      importRowError: '有问题',
      importRowNew: '新建',
      importRowExisting: '更新',
      importCommit: '确认导入',
      importCommitHint: '校验没通过的行不会被写入，其余照常导入。',
      importDone: (created: number, updated: number, skipped: number) =>
        `新建 ${created} 个账号，更新 ${updated} 个，跳过 ${skipped} 行`,
      importNoCodes: '本次没有新建账号，因此没有新的激活码。',
      importClose: '完成',
      importBack: '重新选择文件',
      importFailed: '导入失败',

      empty: '名单还是空的。用「导入名单」把参赛者加进来。',
      emptyFiltered: '当前筛选条件下没有匹配的参赛者。',
    },

    /**
     * 后台账号管理（§5「管理管理员账号」）。
     *
     * 这一页的文案有一条主线：**说清「为什么这个按钮点不动」**。
     * 自我操作与最后一个超管都会被按钮前置灰拦下，如果只灰不说，
     * 会被当成 bug。
     */
    accounts: {
      title: '账号管理',
      subtitle: '管理超级管理员与审核员。参赛者在名单页管理。',

      add: '添加账号',
      search: '按学号或姓名搜索',
      allRoles: '全部角色',

      studentId: '学号',
      name: '姓名',
      role: '角色',
      status: '状态',
      passwordChangedAt: '密码更新',
      createdAt: '创建时间',
      actions: '操作',
      self: '本人',
      neverChanged: '未设置',

      roleLabel: {
        reviewer: '审核员',
        super_admin: '超级管理员',
      },
      statusLabel: {
        active: '正常',
        disabled: '已禁用',
      },
      roleOptions: {
        reviewer: '设为审核员',
        super_admin: '设为超级管理员',
      },

      activeSuperAdminHint: (count: number) => `当前活跃超级管理员 ${count} 人，系统始终保留至少 1 人。`,
      lastSuperAdminAlert:
        '只剩一个活跃超级管理员，因此不能再禁用它或把它降级 —— 否则没人能再进后台。请先添加或启用另一个超管。',

      resetPassword: '重置密码',
      disable: '禁用',
      enable: '启用',

      selfActionTip: '不能对自己的账号执行这个操作，请让其他超级管理员处理',
      lastSuperAdminTip: '系统至少要保留一个活跃的超级管理员',
      /** 自我操作里改名是允许的，所以提示要说清禁止的是哪几件事 */
      selfRenameAllowedTip: '可以改自己的姓名；禁用、降级与重置密码需要其他超管操作',

      resetConfirm: '重置密码？',
      resetConfirmBody: '会生成一个临时密码并撤销该账号的所有登录状态，旧密码同时失效。',
      resetDone: '已生成临时密码',
      disableConfirm: '禁用这个账号？',
      disableConfirmBody: '该账号将无法登录，已登录的会话会被立即撤销。',
      disableDone: '账号已禁用',
      disableRevoked: (n: number) => `已一并撤销 ${n} 个登录会话`,
      enableDone: '账号已启用',
      roleConfirm: (label: string) => `${label}？`,
      roleConfirmBody: '权限在下一个请求即生效。降级不会撤销对方的登录状态，只是不再拥有后台管理权限。',
      roleDone: '角色已修改',

      passwordTitle: '临时密码（只显示这一次）',
      passwordWarning: '关闭后无法再查看。请通过可靠渠道交给本人，并提醒对方尽快修改。',

      empty: '还没有后台账号。',
      emptyFiltered: '当前筛选条件下没有匹配的账号。',
    },

    ops: {
      title: '异常处理',
      subtitle: '以下操作不可逆，每一项都必须填写原因并会写入审计日志。',
      manual: '管理员补录',
      manualHint: '为指定参赛者在指定活动日补录一条记录。该槽位已有记录时会冲突。',
      reopen: '重新开放',
      reopenHint: '让参赛者在该活动日截止后仍可重新提交。必须带时限，时限用完即失效。',
      revoke: '撤销审核结果',
      revokeHint: '只对已通过的记录有效。撤销后该记录不再计分。',
      void: '作废记录',
      voidHint: '任意状态均可作废，不可恢复。',
      adjustment: '积分调整',
      adjustmentHint: '只新增调整记录，不会修改原始积分。调整不受赛道积分上限约束。',

      participant: '参赛者',
      participantSearch: '按学号或姓名搜索',
      track: '赛道',
      overall: '总榜',
      activityDate: '活动日',
      entryId: '记录 ID',
      entryIdPlaceholder: '从审核页复制记录 ID',
      entryNotFound: '没找到这条记录',
      entryNotFoundDetail: '确认 ID 是否抄错。记录被作废或删除后也会查不到。',
      revokeNeedsApproved: '撤销只对「已通过」的记录有效，当前状态不适用。',
      reopenMinutes: '重开时长（分钟）',
      reopenMinutesHint: '默认 120 分钟，最长 7 天',
      points: '调整分值',
      pointsHint: '正数加分、负数扣分。内部按毫点存储，这里填「分」。',
      pointsMilli: (milli: number) => `= ${milli} 毫点`,
      status: '补录后的状态',
      statusPending: '待审核',
      statusApproved: '直接通过',

      consequenceManual: '补录会占用该参赛者在该活动日的这个赛道槽位，之后他无法再自行提交。',
      consequenceReopen: '参赛者将能在时限内重新提交，审核以最新版本为准。',
      consequenceRevoke: '该记录立即停止计分，已发布的排行榜不会自动更新，需要重新计算。',
      consequenceVoid: '该记录永久失效，不可恢复。',
      consequenceAdjustment: '调整立即计入总分，但已发布的排行榜不会自动更新，需要重新计算。',

      done: {
        manual: '已补录',
        reopen: '已重新开放',
        revoke: '已撤销',
        void: '已作废',
        adjustment: '已调整积分',
      },

      note: '补录备注',
      notePlaceholder: '可留空。会写进这条记录的版本说明，供后续审核参考',
      submit: '执行',

      leaderboard: '排行榜维护',
      leaderboardHint: '排行榜是每日快照。改动计分规则或积分后，已发布的榜单不会自动更新。',
      rebuild: '重算排行榜',
      rebuildHint:
        '按当前规则重新计算某一天的快照。同一天已有快照时覆盖它，不会产生第二份；不填统计截止日时算到此刻应有的最新一天，不会倒退到更早的日期。',
      freeze: '冻结最终榜单',
      freezeHint: '冻结后该快照成为最终结果，重算会被拒绝，导出以它为准。',
      unfreeze: '解冻榜单',
      unfreezeHint: '允许该日期的快照被重新计算或整点任务覆盖。',
      cutoffDate: '统计截止日',
      cutoffDateHint: '留空由服务端决定：重算算到此刻应有的最新一天，冻结取最新一份快照。',
      cutoffDateRequired: '解冻必须指定统计截止日，解冻哪一份不能靠猜',

      consequenceRebuild:
        '会用当前规则覆盖该截止日的快照；不填日期时算到此刻应有的最新一天。若榜单已冻结，重算会被拒绝。',
      consequenceFreeze: '冻结之后，这一份就是最终结果。此后重算会被拒绝，需要先解冻。',
      consequenceUnfreeze: '解冻后这份榜单可以再次被重算，也可能被整点的快照任务覆盖。',

      doneRebuild: (rows: number) => `已重算，写入 ${rows} 行`,
      doneFreeze: (rows: number) => `已冻结，共 ${rows} 行`,
      doneUnfreeze: '已解冻，可以重新计算了',
    },

    audit: {
      title: '审计日志',
      subtitle: '只追加，不可修改或删除。',
      actor: '操作人',
      actorPlaceholder: '按操作人 ID 筛选',
      time: '时间',
      action: '动作',
      actionPlaceholder: '如 review.approve',
      targetType: '目标类型',
      targetTypePlaceholder: '如 participant',
      targetId: '目标 ID',
      targetIdPlaceholder: '如记录 ID',
      target: '操作对象',
      dateRange: '时间范围',
      query: '查询',
      reset: '重置',
      noChange: '这条记录没有修改前后的数据。',
      system: '系统',
      before: '修改前',
      after: '修改后',
      emptyValue: '（无）',
      requestId: '追踪号',
      loadFailed: '没能加载审计日志',
      actionLabel: {
        'review.approve': '审核通过',
        'review.reject': '审核驳回',
        'review.reopen': '重新开放',
        'review.revoke': '撤销审核',
        'review.void': '作废记录',
        'review.manual': '管理员补录',
        'score.adjust': '积分调整',
        'campaign.create': '创建活动',
        'campaign.update': '修改活动',
        'campaign.track.update': '修改赛道规则',
        'campaign.status.auto': '活动状态自动流转',
        'participant.import.commit': '导入名单',
        'participant.create': '添加参赛者',
        'participant.status.update': '启用/禁用参赛者',
        'participant.activation.regenerate': '重发激活码',
        'participant.password.reset': '重置密码',
        'participant.anonymize': '匿名化参赛者',
        'account.create': '新建后台账号',
        'account.update': '修改后台账号',
        'account.status.update': '启用/禁用后台账号',
        'account.password.reset': '重置后台账号密码',
        'leaderboard.rebuild': '重算排行榜',
        'leaderboard.freeze': '冻结榜单',
        'leaderboard.unfreeze': '解冻榜单',
        'export.checkins': '导出打卡明细',
        'export.leaderboard': '导出排行榜',
        'export.participants': '导出参赛者名册',
        'job.trigger': '手动触发任务',
        'evidence.purge': '按保留期清理材料',
      },
    },
  },

  /**
   * 操作指导（`src/features/guide/`）。
   *
   * 每个浏览器只演示一遍，之后只能从「我的 → 操作指导」重看。
   * 因此这里**只讲三件没人说就不会知道、且踩了有代价的事**：
   * 新用户要先激活、打卡的流转与截止、排行榜怎么算分。
   * 界面上已经写着的（按钮叫什么、卡片在哪）一律不重复讲。
   *
   * 手机是主场景，每条正文都要能在 375px 上一屏读完。
   */
  tour: {
    /** 单步引导的收尾按钮；多步引导沿用 antd 自带的「下一步 / 上一步」 */
    done: '知道了',

    login: {
      title: '新用户请先激活',
      step: '第 1 步，共 2 步',
      body: '第一次使用请先点「去激活」，用管理员发给你的学号和激活码完成激活，之后就能用学号和密码登录了。',
    },

    activate: {
      title: '填写管理员发的激活码',
      step: '第 2 步，共 2 步',
      body: '激活码由管理员发放，一次性使用。填好学号、激活码，再设置一个密码就完成激活了。',
    },

    home: {
      headerTitle: '每天到点截止，过时不能补交',
      /** 截止时刻取自活动配置，与卡片上的「今日截止」同源 */
      headerBody: (deadline: string) =>
        `每天 ${deadline} 截止。过了截止时间，当天就不能再提交了，会记为「今日未完成」，也无法补交。`,
      cardsTitle: '打卡方法',
      cardsBody: '每个赛道每天打卡一次。点「去打卡」上传证明材料并提交，之后等待审核。',
      statusTitle: '状态怎么看，被驳回怎么办',
      statusBody:
        '卡片右上角是当天的状态：今日尚未打卡 → 已提交，等待审核 → 今日打卡有效 或 已驳回。被驳回后当天点「重新提交」即可，审核只以最新一次为准。',
    },

    leaderboard: {
      title: '每天更新一次，各赛道等权',
      /** 更新时刻取自活动配置，与页面上那行说明同源 */
      body: (time: string) =>
        `排行榜每天 ${time} 更新，统计到前一天 —— 今天刚通过的记录，要等下一次更新才会上榜。各赛道等权：每通过一次打卡得 1 分，总榜是各赛道分数之和。`,
    },
  },

  error: {
    pageTitle: '页面出错了',
    pageSubtitle: '请刷新重试。若持续出现，请把下面的信息发给活动管理员。',
    reload: '刷新页面',
    notFoundTitle: '页面不存在',
    notFoundSubtitle: '链接可能已经失效，或者地址输错了。',
    unknown: '出了点问题，请稍后重试',
  },

  /**
   * 错误码 → 用户可见文案。
   *
   * 只覆盖需要改写服务端措辞的那些；其余直接用服务端返回的 message
   * （它通常更准，含具体上下文）。分支一律依据稳定的 code，
   * 文案只是展示层的事（design.md §12.5）。
   */
  errorCode: {
    TOKEN_INVALID: '登录状态已失效，请重新登录',
    ACCOUNT_DISABLED: '账号已被禁用，请联系管理员',
    ACCOUNT_NOT_ACTIVATED: '账号尚未激活',
    ACTIVATION_INVALID: '学号或激活码不正确，或激活码已失效',
    CHECKIN_CLOSED: '该活动日的打卡已经截止',
    CHECKIN_NOT_OPEN: '今日打卡尚未开放',
    CHECKIN_ALREADY_APPROVED: '该记录已审核通过，如需修改请联系管理员重新打开',
    CAMPAIGN_NOT_ACTIVE: '活动当前不在打卡进行中',
    TRACK_DISABLED: '该赛道当前未开放打卡',
    CAMPAIGN_FROZEN: '活动已结束',
    FORBIDDEN: '没有权限执行该操作',
    ROLE_REQUIRED: '当前账号权限不足',
    CAPABILITY_REQUIRED: '当前账号没有执行该操作的权限',
    NOT_ENTRY_OWNER: '只能查看自己的证明材料',
    NOT_FOUND: '内容不存在或已被删除',
    SIGNATURE_INVALID: '图片地址已失效，正在重新加载',
    RATE_LIMITED: '操作过于频繁，请稍后再试',
    REVIEW_CONFLICT: '该记录在你打开后被修改过，请刷新后重试',
    PENDING_REVIEWS_REMAIN: '仍有待审核记录，请先完成审核',
    SNAPSHOT_FINALIZED: '该榜单已冻结，如需重算请先解冻',
    IMPORT_ALREADY_COMMITTED: '该批次已经导入过了',
    INTERNAL_ERROR: '服务器出错了，请稍后重试',
  },
} as const

export type Messages = typeof zh
